/**
 * Live pipeline-review data for the Verification tab and the nav-chip counts
 * (inc5 step F): the latest pipeline verification run's customer decisions —
 * offered corrections awaiting approval plus guidance rows — and the applied
 * auto-clear disclosure. Failed corrections are deliberately NOT surfaced
 * here: they are builder alerting (run records + logs), never customer noise.
 *
 * One subscription chain shared by both consumers so the headline and the
 * chip can never disagree about the pipeline family.
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  extractPipelineGuidanceRows,
  extractReviewRows,
  type CorrectionDecisionLike,
  type GuidanceRow,
  type RecordCheckLike,
  type ReviewRow,
} from './correctionRows';
import { autoClearDisclosureLines } from './correctionCopy';
import { s3RecordNote } from './s3RecordNote';
import {
  GENERATION_CHECK_DOCS,
  generationCheckRows,
  isRowDismissed,
  type StoredGenerationDoc,
} from '../verification/generationCheck';

export interface PipelineReviewData {
  runId: string | null;
  /**
   * When the latest pipeline run finished, or null if none has.
   *
   * Exposed for the Verification tab's "Last run" line. A manual re-run calls
   * `runValidationV2` then `runPipelineVerificationV2`, and the second finishes
   * roughly 13 seconds after the first (measured 2026-09-02: 17:56:20.582 vs
   * 17:56:35.283). Reading only the validation stamp made the counter say
   * "16s ago" at the moment the run actually completed.
   */
  completedAt: Date | null;
  checks: RecordCheckLike[];
  decisions: CorrectionDecisionLike[];
  /** Open, approvable offers (undeclined ones count; declined render muted). */
  offers: ReviewRow[];
  guidance: GuidanceRow[];
  /** Undeclined offers + guidance rows — what joins the shared count. */
  openCount: number;
  /**
   * What the applied not-submitted clear did this run — cleared, kept because
   * the sponsor's dictionary supplied it, no longer found — as disclosure
   * lines. Null when the clear did not apply or there is nothing to say.
   */
  autoClearLines: string[] | null;
  /** Set when the saved SDTM comparison is an older, capped record that may be missing items. */
  s3Note: string | null;
}

export function usePipelineReview(projectId?: string): PipelineReviewData {
  const [runId, setRunId] = useState<string | null>(null);
  const [completedAt, setCompletedAt] = useState<Date | null>(null);
  const [checks, setChecks] = useState<RecordCheckLike[]>([]);
  const [checksVersion, setChecksVersion] = useState<number | null>(null);
  const [decisions, setDecisions] = useState<CorrectionDecisionLike[]>([]);
  const [generated, setGenerated] = useState<Record<string, StoredGenerationDoc>>({});
  const [dismissals, setDismissals] = useState<Array<{ key: string; fingerprint: string }>>([]);

  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      doc(db, 'projects', projectId, 'pipelineVerification', 'latest'),
      (snap) => {
        const d = snap.exists() ? snap.data() : null;
        setRunId(d ? String(d.runId ?? '') || null : null);
        // Written by pipelineVerification.ts alongside runId on every run.
        const stamp = d?.completedAt as { toDate?: () => Date } | undefined;
        setCompletedAt(stamp?.toDate?.() ?? null);
      },
      () => {
        setRunId(null);
        setCompletedAt(null);
      },
    );
    return () => unsub();
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !runId) {
      setChecks([]);
      setChecksVersion(null);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'projects', projectId, 'pipelineVerificationRuns', runId),
      (snap) => {
        setChecks(snap.exists() ? ((snap.data().checks ?? []) as RecordCheckLike[]) : []);
        const v = snap.exists() ? snap.data().generator?.checksVersion : null;
        setChecksVersion(typeof v === 'number' ? v : null);
      },
      () => {
        setChecks([]);
        setChecksVersion(null);
      },
    );
    return () => unsub();
  }, [projectId, runId]);

  // The checks stored on each generated document (read-only; firestore.rules
  // lets anyone who can open the project read dataManagement/*).
  useEffect(() => {
    if (!projectId) {
      setGenerated({});
      return;
    }
    const unsubs = GENERATION_CHECK_DOCS.map(({ docId }) =>
      onSnapshot(
        doc(db, 'projects', projectId, 'dataManagement', docId),
        (snap) => setGenerated((prev) => ({ ...prev, [docId]: snap.exists() ? (snap.data() as StoredGenerationDoc) : null })),
        () => setGenerated((prev) => ({ ...prev, [docId]: null })),
      ),
    );
    return () => unsubs.forEach((u) => u());
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      collection(db, 'projects', projectId, 'validationDismissals'),
      (snap) => {
        const out: Array<{ key: string; fingerprint: string }> = [];
        snap.forEach((d) => {
          const x = d.data() as { key?: string; fingerprint?: string };
          if (x?.key) out.push({ key: x.key, fingerprint: x.fingerprint ?? '' });
        });
        setDismissals(out);
      },
      () => setDismissals([]),
    );
    return () => unsub();
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      collection(db, 'projects', projectId, 'correctionDecisions'),
      (snap) => {
        const out: CorrectionDecisionLike[] = [];
        snap.forEach((d) => out.push(d.data() as CorrectionDecisionLike));
        setDecisions(out);
      },
      () => setDecisions([]),
    );
    return () => unsub();
  }, [projectId]);

  return useMemo(() => {
    const offers = extractReviewRows([{ family: 'pipeline', checks }], decisions).filter(
      (r) => r.kind === 'offered',
    );
    // Guidance rows share the tab's dismissal store (key = checkId:subject,
    // re-surfaced on fingerprint change — the wave contract, reused).
    const dismissed = new Set(dismissals.map((d) => `${d.key}\u001f${d.fingerprint}`));
    const guidance = [
      ...extractPipelineGuidanceRows(checks),
      ...GENERATION_CHECK_DOCS.flatMap(({ docId }) => generationCheckRows(docId, generated[docId])),
    ].filter(
      (g) => !isRowDismissed(g, dismissals) && !(g.checkId && dismissed.has(`${g.checkId}:${g.subject}\u001f${g.fingerprint ?? ''}`)),
    );
    const openCount = offers.filter((r) => !r.declined).length + guidance.length;

    const s3 = checks.find((c) => c.checkId === 'S3');
    let autoClearLines: string[] | null = null;
    const primary = s3?.correction;
    if (primary?.correctionId === 'dd-sdtm-notsubmitted-clear' && primary.status === 'applied') {
      const lines = autoClearDisclosureLines(primary.summary);
      autoClearLines = lines.length > 0 ? lines : null;
    }
    const s3Note = s3 ? s3RecordNote(checksVersion, s3.items?.length ?? 0) : null;
    return { runId, completedAt, checks, decisions, offers, guidance, openCount, autoClearLines, s3Note };
  }, [runId, completedAt, checks, checksVersion, decisions, dismissals, generated]);
}
